import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} viewport – The Embla viewport element.
 * @property {HTMLElement} container – The Embla container element.
 * @property {HTMLElement[]} slides – The Embla slide elements.
 * @property {HTMLButtonElement} prevButton – The previous button.
 * @property {HTMLButtonElement} nextButton – The next button.
 * @property {HTMLElement} pagination – The pagination container.
 * @property {HTMLButtonElement[]} dots – The pagination dot buttons.
 */

/**
 * A reusable slider component using Embla Carousel.
 *
 * @extends Component<Refs>
 */
class SliderComponent extends Component {
  /** @type {any} */
  #embla = null;
  /** @type {number | null} */
  #autoRotateIntervalId = null;
  #autoRotateEnabled = false;
  /** @type {IntersectionObserver | null} */
  #intersectAutoScrollObserver = null;
  /** @type {number | null} */
  #intersectAutoScrollTimeoutId = null;
  /** @type {number | null} */
  #intersectAutoScrollIntervalId = null;
  #hasTriggeredIntersectAutoScroll = false;
  #isIntersectAutoScrollActive = false;
  /** @type {(event: Event) => void} */
  #boundHandleBlockSelect = () => { };

  async connectedCallback() {
    super.connectedCallback();
    this.#initEmbla();
    this.#initEditorEvents();
    this.#setupIntersectAutoScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEditorEvents();
    this.#stopAutoRotate();
    this.#teardownIntersectAutoScroll();
    if (this.#embla) {
      this.#embla.destroy();
      this.#embla = null;
    }
  }

  /**
   * Initialise Embla Carousel
   */
  #initEmbla() {
    // Détruire l'instance existante si elle existe
    if (this.#embla) {
      this.#stopAutoRotate();
      this.#embla.destroy();
      this.#embla = null;
    }

    const { viewport, container, prevButton, nextButton } = this.refs;
    if (!viewport || !container) return;

    // Vérifier que EmblaCarousel est disponible (chargé depuis le CDN)
    const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
    if (typeof EmblaCarousel === 'undefined') {
      console.warn('EmblaCarousel is not loaded yet');
      return;
    }

    // Récupérer le plugin WheelGestures s'il est disponible
    const WheelGesturesPlugin = /** @type {any} */ (window).EmblaCarouselWheelGestures;

    // Récupérer les options depuis les attributs
    const align = this.getAttribute('align') || 'start';
    const containScroll = this.getAttribute('contain-scroll') || 'trimSnaps';
    const dragFree = this.getAttribute('drag-free') === 'true';
    const slidesToScroll = parseInt(this.getAttribute('slides-to-scroll') || '1', 10);
    const loop = this.getAttribute('loop') === 'true';
    const breakpoints = this.#parseBreakpoints();

    // Déterminer la valeur active par défaut (mobile)
    const activeDefaultAttr = this.getAttribute('active-default');
    const active = activeDefaultAttr !== null ? activeDefaultAttr !== 'false' : true;

    // Préparer les plugins (wheel gestures pour le scroll horizontal avec trackpad)
    const plugins = [];
    if (WheelGesturesPlugin) {
      plugins.push(WheelGesturesPlugin());
    }

    // Initialiser Embla avec les plugins
    this.#embla = EmblaCarousel(viewport, {
      active,
      align,
      containScroll,
      dragFree,
      loop,
      slidesToScroll,
      breakpoints,
    }, plugins);

    this.#embla.on('init', () => this.setAttribute('initialized', ''));

    // Gérer les boutons de navigation
    this.#setupNavigation();

    // Gérer la pagination (dots)
    this.#setupPagination();

    // Gérer l'auto-rotation (optionnelle)
    this.#setupAutoRotate();
  }

  /**
   * Configure les boutons de navigation
   */
  #setupNavigation() {
    const { prevButton, nextButton } = this.refs;
    if (!prevButton || !nextButton || !this.#embla) return;

    const SNAP_TOLERANCE = 0.005;

    const updateButtonStates = () => {
      if (!this.#embla) return;
      const snapList = this.#embla.scrollSnapList();
      const selectedIndex = this.#embla.selectedScrollSnap();
      const currentPos = snapList[selectedIndex];

      // En mode loop, on peut toujours aller dans les deux sens
      if (this.#isLoopEnabled() && snapList.length > 1) {
        prevButton.disabled = false;
        nextButton.disabled = false;
        return;
      }

      const hasPrevSnap = snapList.some(
        (/** @type {number} */ pos, /** @type {number} */ i) => i < selectedIndex && Math.abs(pos - currentPos) > SNAP_TOLERANCE
      );
      const hasNextSnap = snapList.some(
        (/** @type {number} */ pos, /** @type {number} */ i) => i > selectedIndex && Math.abs(pos - currentPos) > SNAP_TOLERANCE
      );

      prevButton.disabled = !hasPrevSnap;
      nextButton.disabled = !hasNextSnap;
    };

    this.#embla.on('init', updateButtonStates);
    this.#embla.on('select', updateButtonStates);
    this.#embla.on('settle', updateButtonStates);
    this.#embla.on('reInit', updateButtonStates);
    updateButtonStates();
  }

  /**
   * Indique si le slider est en mode loop
   * @returns {boolean}
   */
  #isLoopEnabled() {
    return this.getAttribute('loop') === 'true';
  }

  /**
   * Gère le clic sur le bouton précédent
   */
  handlePrevClick() {
    if (!this.#embla) return;

    if (this.#isLoopEnabled()) {
      this.#embla.scrollPrev();
      this.#restartAutoRotate();
      return;
    }

    const target = this.#findVisualSnap(-1);
    if (target !== null) {
      this.#embla.scrollTo(target);
      this.#restartAutoRotate();
    }
  }

  /**
   * Gère le clic sur le bouton suivant
   */
  handleNextClick() {
    if (!this.#embla) return;

    if (this.#isLoopEnabled()) {
      this.#embla.scrollNext();
      this.#restartAutoRotate();
      return;
    }

    const target = this.#findVisualSnap(1);
    if (target !== null) {
      this.#embla.scrollTo(target);
      this.#restartAutoRotate();
    }
  }

  /**
   * Trouve le prochain snap avec une position visuellement différente
   * @param {1 | -1} direction
   * @returns {number | null}
   */
  #findVisualSnap(direction) {
    if (!this.#embla) return null;
    const SNAP_TOLERANCE = 0.005;
    const snapList = this.#embla.scrollSnapList();
    const current = this.#embla.selectedScrollSnap();
    const currentPos = snapList[current];

    if (direction === 1) {
      for (let i = current + 1; i < snapList.length; i++) {
        if (Math.abs(snapList[i] - currentPos) > SNAP_TOLERANCE) return i;
      }
    } else {
      for (let i = current - 1; i >= 0; i--) {
        if (Math.abs(snapList[i] - currentPos) > SNAP_TOLERANCE) return i;
      }
    }
    return null;
  }

  /**
   * Configure la pagination avec les dots
   */
  #setupPagination() {
    const { dots } = this.refs;
    if (!dots || dots.length === 0 || !this.#embla) return;

    const activeClass = this.getAttribute('dot-active-class') || 'transformation-showcase__dot--active';

    const updateDots = () => {
      if (!this.#embla) return;
      const selectedIndex = this.#embla.selectedScrollSnap();

      dots.forEach((dot, index) => {
        dot.classList.toggle(activeClass, index === selectedIndex);
      });
    };

    // Mettre à jour les dots sur les événements Embla
    this.#embla.on('init', updateDots);
    this.#embla.on('select', updateDots);
    this.#embla.on('settle', updateDots);
    this.#embla.on('reInit', updateDots);
    updateDots();
  }

  /**
   * Gère le clic sur un dot de pagination
   * @param {number} index - L'index de la slide à afficher
   */
  handleDotClick(index) {
    if (this.#embla) {
      this.#embla.scrollTo(index);
      this.#restartAutoRotate();
    }
  }

  /**
   * Parse breakpoints from data-breakpoints attribute
   * @returns {Record<string, any>}
   */
  #parseBreakpoints() {
    const breakpointsAttr = this.getAttribute('data-breakpoints');
    if (!breakpointsAttr) return {};

    try {
      return JSON.parse(breakpointsAttr);
    } catch (error) {
      console.warn('Invalid breakpoints JSON:', error);
      return {};
    }
  }

  /**
   * Configure l'auto-rotation
   */
  #setupAutoRotate() {
    this.#stopAutoRotate();
    if (!this.#embla) return;
    if (this.getAttribute('auto-rotate') !== 'true') return;

    const scrollSnaps = this.#embla.scrollSnapList();
    if (scrollSnaps.length <= 1) return;

    this.#autoRotateEnabled = true;
    this.#startAutoRotateTimer();

    // Reset le timer quand l'utilisateur drag le slider
    this.#embla.on('pointerDown', () => this.#restartAutoRotate());

    // Pause au hover, reprise au leave
    this.addEventListener('mouseenter', () => this.#stopAutoRotate());
    this.addEventListener('mouseleave', () => {
      if (this.#autoRotateEnabled) this.#startAutoRotateTimer();
    });
  }

  /**
   * Start the auto-rotate interval timer
   */
  #startAutoRotateTimer() {
    this.#stopAutoRotate();

    const interval = parseInt(this.getAttribute('auto-rotate-interval') || '5000', 10);
    const intervalMs = Number.isFinite(interval) && interval >= 1000 ? interval : 5000;

    this.#autoRotateIntervalId = window.setInterval(() => {
      if (!this.#embla || document.hidden) return;
      this.#embla.scrollNext();
    }, intervalMs);
  }

  /**
   * Restart the auto-rotate timer (resets the delay)
   */
  #restartAutoRotate() {
    if (!this.#autoRotateEnabled) return;
    this.#startAutoRotateTimer();
  }

  /**
   * Stop auto-rotation timer
   */
  #stopAutoRotate() {
    if (this.#autoRotateIntervalId !== null) {
      window.clearInterval(this.#autoRotateIntervalId);
      this.#autoRotateIntervalId = null;
    }
  }

  #setupIntersectAutoScroll() {
    this.#teardownIntersectAutoScroll();
    this.#hasTriggeredIntersectAutoScroll = false;

    if (this.getAttribute('auto-scroll-on-intersect') !== 'true') return;
    if (window.Shopify?.designMode) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const thresholdAttr = parseFloat(this.getAttribute('auto-scroll-on-intersect-threshold') || '0');
    const threshold = Number.isFinite(thresholdAttr) ? thresholdAttr : 0;
    const rootMargin =
      this.getAttribute('auto-scroll-on-intersect-root-margin') || '-45% 0px -45% 0px';

    this.#intersectAutoScrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#isIntersectAutoScrollActive = entry.isIntersecting;

          if (entry.isIntersecting) {
            this.#triggerIntersectAutoScroll();
            return;
          }

          this.#stopIntersectAutoScrollLoop();
        });
      },
      {
        root: null,
        threshold,
        rootMargin,
      }
    );

    this.#intersectAutoScrollObserver.observe(this);
  }

  #teardownIntersectAutoScroll() {
    if (this.#intersectAutoScrollObserver) {
      this.#intersectAutoScrollObserver.disconnect();
      this.#intersectAutoScrollObserver = null;
    }

    if (this.#intersectAutoScrollTimeoutId !== null) {
      window.clearTimeout(this.#intersectAutoScrollTimeoutId);
      this.#intersectAutoScrollTimeoutId = null;
    }

    this.#stopIntersectAutoScrollLoop();
    this.#isIntersectAutoScrollActive = false;
  }

  #isIntersectAutoScrollRepeatEnabled() {
    return this.getAttribute('auto-scroll-on-intersect-repeat') === 'true';
  }

  #startIntersectAutoScrollLoop() {
    if (!this.#isIntersectAutoScrollRepeatEnabled()) return;
    if (this.#intersectAutoScrollIntervalId !== null) return;

    const intervalAttr = parseInt(
      this.getAttribute('auto-scroll-on-intersect-repeat-interval') || '1200',
      10
    );
    const intervalMs = Number.isFinite(intervalAttr) && intervalAttr >= 150 ? intervalAttr : 1200;

    this.#intersectAutoScrollIntervalId = window.setInterval(() => {
      if (!this.isConnected) return;
      if (!this.#isIntersectAutoScrollActive) return;
      if (document.hidden) return;

      this.#triggerIntersectAutoScroll();
    }, intervalMs);
  }

  #stopIntersectAutoScrollLoop() {
    if (this.#intersectAutoScrollIntervalId !== null) {
      window.clearInterval(this.#intersectAutoScrollIntervalId);
      this.#intersectAutoScrollIntervalId = null;
    }
  }

  #triggerIntersectAutoScroll() {
    const repeatEnabled = this.#isIntersectAutoScrollRepeatEnabled();
    const onceEnabled = !repeatEnabled && this.getAttribute('auto-scroll-on-intersect-once') !== 'false';

    if (this.#hasTriggeredIntersectAutoScroll && onceEnabled) {
      return;
    }

    if (this.getAttribute('auto-scroll-on-intersect-mobile-only') === 'true') {
      if (!window.matchMedia('(max-width: 989px)').matches) return;
    }

    const delayAttr = parseInt(this.getAttribute('auto-scroll-on-intersect-delay') || '0', 10);
    const delay = Number.isFinite(delayAttr) && delayAttr > 0 ? delayAttr : 0;
    const retryDelayAttr = parseInt(
      this.getAttribute('auto-scroll-on-intersect-retry-delay') || '180',
      10
    );
    const retryDelay = Number.isFinite(retryDelayAttr) && retryDelayAttr > 0 ? retryDelayAttr : 180;
    const maxRetriesAttr = parseInt(this.getAttribute('auto-scroll-on-intersect-max-retries') || '6', 10);
    const maxRetries = Number.isFinite(maxRetriesAttr) && maxRetriesAttr >= 0 ? maxRetriesAttr : 6;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const finalize = () => {
      this.#hasTriggeredIntersectAutoScroll = true;

      if (repeatEnabled) {
        this.#startIntersectAutoScrollLoop();
        return;
      }

      if (onceEnabled) {
        this.#intersectAutoScrollObserver?.disconnect();
        this.#intersectAutoScrollObserver = null;
      }
    };

    const attemptScroll = (attempt = 0) => {
      if (!this.isConnected) return;
      if (!this.#embla) {
        if (attempt < maxRetries) {
          this.#intersectAutoScrollTimeoutId = window.setTimeout(() => {
            this.#intersectAutoScrollTimeoutId = null;
            attemptScroll(attempt + 1);
          }, retryDelay);
        }
        return;
      }

      const scrollSnaps = this.#embla.scrollSnapList();
      const hasMultipleSnaps = Array.isArray(scrollSnaps) && scrollSnaps.length > 1;
      if (!hasMultipleSnaps) {
        if (attempt < maxRetries) {
          this.#intersectAutoScrollTimeoutId = window.setTimeout(() => {
            this.#intersectAutoScrollTimeoutId = null;
            attemptScroll(attempt + 1);
          }, retryDelay);
          return;
        }

        finalize();
        return;
      }

      const canScrollNext =
        typeof this.#embla.canScrollNext === 'function' ? this.#embla.canScrollNext() : true;

      if (!canScrollNext) {
        this.#stopIntersectAutoScrollLoop();
        finalize();
        return;
      }

      this.#embla.scrollNext();
      finalize();
    };

    if (delay === 0) {
      attemptScroll();
      return;
    }

    if (this.#intersectAutoScrollTimeoutId !== null) return;

    this.#intersectAutoScrollTimeoutId = window.setTimeout(() => {
      this.#intersectAutoScrollTimeoutId = null;
      attemptScroll();
    }, delay);
  }

  /**
   * Réinitialise le slider (utile après un morph)
   */
  reInit() {
    this.#initEmbla();
    this.#setupIntersectAutoScroll();
  }

  /**
   * Initialize Shopify theme editor events
   */
  #initEditorEvents() {
    if (!window.Shopify?.designMode) return;

    this.#boundHandleBlockSelect = (event) => this.#handleBlockSelect(event);
    document.addEventListener('shopify:block:select', this.#boundHandleBlockSelect);
  }

  /**
   * Remove Shopify theme editor events
   */
  #removeEditorEvents() {
    document.removeEventListener('shopify:block:select', this.#boundHandleBlockSelect);
  }

  /**
   * Handle block selection in theme editor
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    if (!this.#embla) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!this.contains(target)) return;

    const container = this.refs.container;
    if (!container || Array.isArray(container)) return;

    /** @type {HTMLElement | null} */
    let slide = target;
    while (slide && slide.parentElement !== container) {
      slide = slide.parentElement;
    }
    if (!slide) return;

    const slides = Array.from(container.children);
    const index = slides.indexOf(slide);
    if (index < 0) return;

    this.#embla.scrollTo(index);
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}
